import { Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle } from 'lucide-react'
import { get } from '~/lib/api'
import { useCurrentUser } from '~/lib/auth'
import type { DashboardPayload } from '~/lib/types'
import StatTile from '~/components/StatTile'
import FunnelChart from '~/components/FunnelChart'
import ActivityChart from '~/components/ActivityChart'
import StageBadge from '~/components/StageBadge'
import DemoBadge from '~/components/DemoBadge'
import Spinner from '~/components/Spinner'
import { Button } from '~/components/ui/button'
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '~/components/ui/card'
import { Skeleton } from '~/components/ui/skeleton'
import { formatAgo } from '~/lib/format'

export const dashboardQuery = {
  queryKey: ['dashboard'],
  queryFn: () => get<DashboardPayload>('/api/v1/dashboard'),
}

export default function Dashboard() {
  const { data: user } = useCurrentUser()
  const { data, isPending, isError, error } = useQuery(dashboardQuery)

  if (isPending) return <DashboardSkeleton />

  if (isError) {
    return (
      <p role="alert" className="rounded-xl border border-line bg-surface p-5 text-sm text-critical">
        {error.message}
      </p>
    )
  }

  const firstName = user?.name?.split(' ')[0]

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {firstName ? `Welcome back, ${firstName}` : 'Overview'}
          </h1>
          <p className="mt-1 text-sm text-ink-2">
            How the agent is moving registered students toward enrolment.
          </p>
        </div>
        {data.demo && <DemoBadge />}
      </div>

      {data.escalated_count > 0 && <EscalationBanner count={data.escalated_count} />}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {data.stats.map((stat) => (
          <StatTile key={stat.key} stat={stat} />
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-5">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-sm">Pipeline</CardTitle>
            <CardDescription>
              Students who reached each stage &middot; {data.lost_count} lost, excluded
            </CardDescription>
          </CardHeader>
          <CardContent>
            <FunnelChart steps={data.funnel} />
          </CardContent>
        </Card>

        <Card className="lg:col-span-3">
          <CardHeader>
            <CardTitle className="text-sm">Message volume</CardTitle>
            <CardDescription>Last {data.activity_series.length} days</CardDescription>
          </CardHeader>
          <CardContent>
            <ActivityChart data={data.activity_series} />
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Live conversations</CardTitle>
          <CardDescription>Newest activity first</CardDescription>
          <CardAction>
            <Button asChild variant="outline" size="sm">
              <Link to="/conversations" search={{ stage: 'all', escalated: false }}>
                View all
              </Link>
            </Button>
          </CardAction>
        </CardHeader>

        <CardContent>
          <ul className="divide-y divide-line">
            {data.recent_conversations.map((row) => (
              <li key={row.student} className="flex flex-wrap items-center gap-3 py-3 first:pt-0">
                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-2 text-sm font-medium">
                    {row.student}
                    {row.escalated && (
                      <span className="inline-flex items-center gap-1 text-xs text-warning">
                        <AlertTriangle className="size-3" aria-hidden="true" />
                        Needs a human
                      </span>
                    )}
                  </p>
                  <p className="truncate text-xs text-faint">
                    {row.course} &middot; {row.last_message}
                  </p>
                </div>

                <StageBadge stage={row.stage} />
                <span className="w-16 text-right text-xs text-faint [font-variant-numeric:tabular-nums]">
                  {formatAgo(row.minutes_ago)}
                </span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  )
}

function EscalationBanner({ count }: { count: number }) {
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-xl border border-line bg-surface p-4">
      <AlertTriangle className="size-4 shrink-0 text-warning" aria-hidden="true" />
      <p className="flex-1 text-sm">
        <span className="font-medium">
          {count} conversation{count === 1 ? '' : 's'} escalated
        </span>
        <span className="text-ink-2"> — the agent handed these to a person.</span>
      </p>
      <Button asChild size="sm" variant="outline">
        <Link to="/conversations" search={{ stage: 'all', escalated: true }}>
          Review
        </Link>
      </Button>
    </div>
  )
}

function DashboardSkeleton() {
  return (
    <div className="flex flex-col gap-6">
      <Skeleton className="h-8 w-64" />
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-28" />
        ))}
      </div>
      <div className="grid gap-6 lg:grid-cols-5">
        <Skeleton className="h-72 lg:col-span-2" />
        <Skeleton className="h-72 lg:col-span-3" />
      </div>
      <div className="grid place-items-center py-4">
        <Spinner label="Loading dashboard" />
      </div>
    </div>
  )
}
